import { makeStyles, mergeClasses, tokens, Tooltip } from "@fluentui/react-components";
import type { CSSProperties, ReactNode } from "react";

export type StatTone = "danger" | "warning" | "success" | "brand" | "muted";

export interface StatItem {
  id?: string;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: StatTone;
  onClick?: () => void;
}

export interface StatRibbonProps {
  items: StatItem[];
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  trailing?: ReactNode;
}

const useStyles = makeStyles({
  ribbon: {
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    width: "100%",
    minWidth: 0,
    height: "28px",
    boxSizing: "border-box",
    overflow: "hidden",
    columnGap: "2px",
    padding: "0 6px",
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalS,
    whiteSpace: "nowrap",
  },
  slot: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    flex: "0 0 auto",
    minWidth: 0,
  },
  item: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    flex: "0 0 auto",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    lineHeight: "20px",
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  clickableItem: {
    cursor: "pointer",
    borderRadius: tokens.borderRadiusSmall,
    padding: "1px 4px",
    margin: "-1px -4px",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-1px",
    },
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
  },
  value: {
    color: tokens.colorNeutralForeground1,
    fontSize: "12px",
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  toneDanger: {
    color: tokens.colorPaletteRedForeground1,
  },
  toneWarning: {
    color: tokens.colorPaletteYellowForeground2,
  },
  toneSuccess: {
    color: tokens.colorPaletteGreenForeground1,
  },
  toneBrand: {
    color: tokens.colorBrandForeground1,
  },
  toneMuted: {
    color: tokens.colorNeutralForeground4,
  },
  dot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  dotDanger: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  dotWarning: {
    backgroundColor: tokens.colorPaletteYellowBackground3,
  },
  dotSuccess: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  divider: {
    color: tokens.colorNeutralStroke1,
    fontSize: "10px",
    lineHeight: "1",
    userSelect: "none",
  },
  trailing: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
});

/**
 * 슬림 인라인 스탯 리본 (Inline Stat Ribbon)
 * 툴바 아래 28px 높이의 1줄 컴팩트 요약 바.
 * 정보 밀도를 극대화하고 테이블 위 공간 낭비를 제거합니다.
 */
export function StatRibbon({ items, className, style, ariaLabel = "핵심 지표 요약", trailing }: StatRibbonProps) {
  const styles = useStyles();

  return (
    <div className={mergeClasses(styles.ribbon, className)} style={style} role="region" aria-label={ariaLabel}>
      {items.map((item, idx) => {
        const toneClass =
          item.tone === "danger"
            ? styles.toneDanger
            : item.tone === "warning"
            ? styles.toneWarning
            : item.tone === "success"
            ? styles.toneSuccess
            : item.tone === "brand"
            ? styles.toneBrand
            : item.tone === "muted"
            ? styles.toneMuted
            : undefined;

        const dotClass =
          item.tone === "danger"
            ? styles.dotDanger
            : item.tone === "warning"
            ? styles.dotWarning
            : item.tone === "success"
            ? styles.dotSuccess
            : undefined;

        const itemNode = (
          <span
            className={mergeClasses(styles.item, item.onClick && styles.clickableItem)}
            role={item.onClick ? "button" : undefined}
            tabIndex={item.onClick ? 0 : undefined}
            onClick={item.onClick}
            onKeyDown={
              item.onClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      item.onClick?.();
                    }
                  }
                : undefined
            }
          >
            {dotClass && <span className={mergeClasses(styles.dot, dotClass)} aria-hidden="true" />}
            <span className={styles.label}>{item.label}</span>
            <span className={mergeClasses(styles.value, toneClass)}>{item.value}</span>
          </span>
        );

        return (
          <span key={item.id ?? `${item.label}-${idx}`} className={styles.slot}>
            {idx > 0 && <span className={styles.divider} aria-hidden="true">·</span>}
            {item.hint ? (
              <Tooltip content={<span>{item.hint}</span>} relationship="description" withArrow positioning="above">
                {itemNode}
              </Tooltip>
            ) : (
              itemNode
            )}
          </span>
        );
      })}

      {trailing && <div className={styles.trailing}>{trailing}</div>}
    </div>
  );
}
